import { useMemo } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { TokenCostItem } from "@/hooks/useTokenCost";
import { estimateCost, formatCost } from "./pricing";
import { PieChart as PieChartIcon } from "lucide-react";

const MODEL_COLORS = [
  "hsl(222.2 47.4% 11.2%)",
  "hsl(215 20.2% 65.1%)",
  "hsl(210 40% 80%)",
  "hsl(210 20% 90%)",
];

// --- Model Donut ---

function ModelDonut({ items }: { items: TokenCostItem[] }) {
  const data = useMemo(
    () => items
      .filter((d) => d.model)
      .map((d) => ({
        name: (d.model ?? "").replace(/"/g, ""),
        value: estimateCost(d),
      })),
    [items],
  );

  if (data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">By Model</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="55%"
              outerRadius="80%"
              paddingAngle={2}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={MODEL_COLORS[i % MODEL_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(val: number) => [formatCost(val), "cost"]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-wrap justify-center gap-3 mt-2">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center gap-1.5 text-xs">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: MODEL_COLORS[i % MODEL_COLORS.length] }}
              />
              {d.name}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Phase Bar Chart ---

function PhaseBar({ items }: { items: TokenCostItem[] }) {
  const data = useMemo(
    () => items
      .filter((d) => d.phase)
      .map((d) => ({
        phase: (d.phase ?? "").replace(/"/g, ""),
        cost: estimateCost(d),
      }))
      .sort((a, b) => b.cost - a.cost),
    [items],
  );

  if (data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">By Phase</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data} layout="vertical" margin={{ left: 60 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={formatCost} />
            <YAxis type="category" dataKey="phase" tick={{ fontSize: 11 }} width={55} />
            <Tooltip formatter={(val: number) => formatCost(val)} />
            <Bar dataKey="cost" fill="hsl(222.2 47.4% 11.2%)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// --- Empty State ---

function CostDistributionEmpty() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
      <PieChartIcon className="h-8 w-8 text-muted-foreground/40 mb-3" />
      <h3 className="text-sm font-medium text-foreground mb-1">No cost breakdown available</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Cost distribution by model and phase will appear once token data is recorded.
      </p>
    </div>
  );
}

// --- Composed Widget ---

interface CostDistributionProps {
  modelItems: TokenCostItem[];
  phaseItems: TokenCostItem[];
}

export function CostDistribution({ modelItems, phaseItems }: CostDistributionProps) {
  const hasModelData = modelItems.some((d) => d.model);
  const hasPhaseData = phaseItems.some((d) => d.phase);

  if (!hasModelData && !hasPhaseData) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold tracking-tight">Where the Money Goes</h3>
        <CostDistributionEmpty />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold tracking-tight">Where the Money Goes</h3>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {hasModelData && <ModelDonut items={modelItems} />}
        {hasPhaseData && <PhaseBar items={phaseItems} />}
      </div>
    </div>
  );
}
